import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { fetchTools, getWorkspace, saveWorkspace, deleteWorkspace, startGoogleLogin } from "../lib/api";
import { useAuthUser } from "../lib/useAuthUser";
import { openManagedWindows, popupHelp, POPUP_TOAST_DURATION } from "../lib/pwa";

const MAX_WINDOWS = 8;

/**
 * Personal Workspace editor — pick the specific tools you want, in order; each
 * one becomes its own tiled window. Saved to your account, then launchable in
 * one click from the installed PWA (the "▶ open workspace" action in the stack
 * bar) or right here.
 */
export default function Workspace() {
  const { user, loading } = useAuthUser();
  const [allTools, setAllTools] = useState([]);
  const [slugs, setSlugs] = useState([]); // ordered selected slugs (one per window)
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchTools()
      .then((t) => alive && setAllTools(t || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    getWorkspace()
      .then((w) => {
        if (!alive) return;
        setSlugs((w?.windows || []).map((x) => x.slug));
        setLoaded(true);
      })
      .catch(() => alive && setLoaded(true));
    return () => {
      alive = false;
    };
  }, [user]);

  const bySlug = useMemo(() => {
    const m = {};
    for (const t of allTools) m[t.slug] = t;
    return m;
  }, [allTools]);

  const selected = slugs.map((s) => bySlug[s]).filter(Boolean);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return allTools
      .filter(
        (t) =>
          !slugs.includes(t.slug) &&
          ((t.title || "").toLowerCase().includes(term) ||
            (t.slug || "").toLowerCase().includes(term)),
      )
      .slice(0, 8);
  }, [q, allTools, slugs]);

  const add = (slug) => {
    if (slugs.includes(slug)) return;
    if (slugs.length >= MAX_WINDOWS) {
      toast.error(`max ${MAX_WINDOWS} windows`);
      return;
    }
    setSlugs((p) => [...p, slug]);
    setQ("");
  };
  const remove = (slug) => setSlugs((p) => p.filter((s) => s !== slug));
  const move = (i, dir) => {
    setSlugs((p) => {
      const next = [...p];
      const j = i + dir;
      if (j < 0 || j >= next.length) return p;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveWorkspace({ slugs });
      window.dispatchEvent(new CustomEvent("tv:workspace-changed"));
      toast.success("workspace saved");
    } catch {
      toast.error("could not save");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await deleteWorkspace();
      setSlugs([]);
      window.dispatchEvent(new CustomEvent("tv:workspace-changed"));
      toast.success("workspace cleared");
    } catch {
      toast.error("could not clear");
    } finally {
      setSaving(false);
    }
  };

  const openNow = async () => {
    const items = selected.filter((t) => t.url).map((t, i) => ({ url: t.url, name: `tv_ws_${i}` }));
    if (!items.length) return;
    const opened = await openManagedWindows(items);
    if (opened === 0) {
      toast.error(popupHelp(), { duration: POPUP_TOAST_DURATION });
    } else if (opened < items.length) {
      toast.warning(`Opened ${opened} of ${items.length}. ${popupHelp()}`, { duration: POPUP_TOAST_DURATION });
    } else {
      toast.success(`opened ${items.length} windows`);
    }
  };

  return (
    <div data-testid="workspace-page">
      <Seo title="My Workspace" path="/workspace" noindex />
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-[12px] text-[#888] mb-3">
          <Link to="/" className="tv-link">
            index
          </Link>{" "}
          / workspace
        </div>
        <h1 className="text-xl font-bold border-b border-black pb-2" data-testid="workspace-title">
          {">"}_ my workspace
        </h1>
        <p className="mt-3 text-[13px] text-[#444] leading-relaxed">
          Pick the tools you want open every session — each becomes its own tiled
          window. Launch them all in one click from the{" "}
          <span className="font-bold">▶ open workspace</span> action in the stack
          bar, or from here. Saved to your account.
        </p>

        {!user && !loading ? (
          <div className="mt-6 border border-black p-4" data-testid="workspace-login-gate">
            <p className="text-[13px]">Log in to save a workspace to your account.</p>
            <button
              type="button"
              onClick={() => startGoogleLogin("/workspace")}
              className="tv-btn mt-3"
              data-testid="workspace-login-btn"
            >
              [log in with Google]
            </button>
          </div>
        ) : (
          <>
            {/* Selected windows */}
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#888] mb-2">
                your windows ({selected.length}/{MAX_WINDOWS})
              </div>
              {selected.length === 0 ? (
                <div className="text-[13px] text-[#888] border border-dashed border-[#ccc] p-4" data-testid="workspace-empty">
                  no tools yet — search below to add up to {MAX_WINDOWS}.
                </div>
              ) : (
                <ol className="list-none p-0 m-0 border border-black" data-testid="workspace-selected">
                  {selected.map((t, i) => (
                    <li
                      key={t.slug}
                      className="flex items-center gap-2 px-3 py-2 border-b border-[#eee] last:border-b-0"
                      data-testid={`workspace-item-${t.slug}`}
                    >
                      <span className="text-[12px] text-[#888] tabular-nums w-5">{i + 1}.</span>
                      <span className="text-[13px] font-bold flex-1 truncate">{t.title}</span>
                      <button
                        type="button"
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        className="tv-link text-[13px] disabled:opacity-25"
                        data-testid={`workspace-up-${t.slug}`}
                        aria-label="move up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => move(i, 1)}
                        disabled={i === selected.length - 1}
                        className="tv-link text-[13px] disabled:opacity-25"
                        data-testid={`workspace-down-${t.slug}`}
                        aria-label="move down"
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(t.slug)}
                        className="tv-link text-[13px] text-[#c00]"
                        data-testid={`workspace-remove-${t.slug}`}
                        aria-label="remove"
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {/* Tool search */}
            <div className="mt-5 relative">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="search tools to add…"
                className="w-full border border-black px-3 py-2 text-[13px] bg-white outline-none"
                data-testid="workspace-search"
              />
              {results.length > 0 && (
                <div className="border border-t-0 border-black" data-testid="workspace-results">
                  {results.map((t) => (
                    <button
                      key={t.slug}
                      type="button"
                      onClick={() => add(t.slug)}
                      className="block w-full text-left px-3 py-2 text-[13px] border-b border-[#eee] last:border-b-0 hover:bg-black hover:text-white"
                      data-testid={`workspace-add-${t.slug}`}
                    >
                      + {t.title}{" "}
                      <span className="opacity-50 text-[11px]">{t.category}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="tv-btn"
                data-testid="workspace-save"
              >
                {saving ? "[saving…]" : "[save workspace]"}
              </button>
              <button
                type="button"
                onClick={openNow}
                disabled={selected.length === 0}
                className="tv-btn disabled:opacity-40"
                data-testid="workspace-open-now"
              >
                [▶ open now ({selected.length} windows)]
              </button>
              {loaded && (
                <button
                  type="button"
                  onClick={clear}
                  disabled={saving}
                  className="tv-link text-[13px] self-center"
                  data-testid="workspace-clear"
                >
                  clear workspace
                </button>
              )}
            </div>
            <p className="mt-4 text-[12px] text-[#888] leading-relaxed">
              Tip: the first time you open a workspace, the app asks to{" "}
              <span className="font-bold">manage windows</span> — allow it once and
              every tool opens in its own tiled window from then on (no browser
              pop-up settings needed). Window tiling is a desktop feature.
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
